import { useCallback, useState } from "react";

// "cover" crops to fill the 16:9 tile edge-to-edge (today's only behavior);
// "contain" letterboxes instead, showing the camera's whole picture. Which
// one is right is a property of the individual camera's own aspect ratio and
// what's worth seeing at its edges, not something one setting could get
// right for a whole fleet — hence per-camera rather than a single app-wide
// toggle.
export type VideoFit = "cover" | "contain";

const STORAGE_PREFIX = "mtn-cctv-video-fit:";

function readStored(cameraId: string): VideoFit {
  try {
    const stored = window.localStorage.getItem(STORAGE_PREFIX + cameraId);
    if (stored === "contain") return stored;
  } catch {
    // Private browsing / storage disabled: fall through to the default.
  }
  return "cover";
}

// Persists the viewer's preferred fit for one camera across reloads. Purely
// a per-viewer display preference — nothing here is shared with other
// viewers or synced to the backend, same reasoning as useGridColumns: which
// fit suits a screen is a property of the screen, not the fleet, so it
// needs no backend field and no migration.
export function useVideoFit(cameraId: string): [VideoFit, () => void] {
  const [fit, setFit] = useState<VideoFit>(() => readStored(cameraId));

  const toggle = useCallback(() => {
    setFit((prev) => {
      const next: VideoFit = prev === "cover" ? "contain" : "cover";
      try {
        window.localStorage.setItem(STORAGE_PREFIX + cameraId, next);
      } catch {
        // Best-effort: the in-memory state still updates for this session.
      }
      return next;
    });
  }, [cameraId]);

  return [fit, toggle];
}
